import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { ddRum, rumErrorCtx, SEVERITY } from '@web-platform/browser-rum';
import { initGrabIDAuthClient } from 'helper/grab-id-auth-helper';
import { redirectToLandingPageIfNoCountry } from 'helper/redirect-helper';
import { paxLoginStartAction } from 'actions/login-actions';
import {
  STORAGE_LOGIN_REDIRECT,
  GRAB_ID_LOGIN_METHOD,
  APP_BASE_URL_PATH,
} from 'constants/app-constants';
import {
  SCRIBE_MEX_APP_STATES,
  SCRIBE_MEX_APP_EVENTS,
  SCRIBE_EVENT_PARAMS,
  getCommonMexAppFacet,
  scribeTrackEvent,
} from 'applications/scribe/instrumentations/track';
import { isFromMexApp } from 'helper/mex-app-helper';
import { isUserAuthenticatedAsync } from 'helper/auth-helper';
import { ROUTES_V2_PATHS } from '../constants/route-constants';
import {
  clearLoginStorage,
  getLoginRedirectUrl,
} from '../helper/session-helper';

const LoginViewV2 = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const method = queryParams.get('method');
  const loginRedirect = queryParams.get('redirect');

  const captureException = useCallback(
    error => {
      const isMexApp = isFromMexApp();
      const errorCtx = rumErrorCtx()
        .component('LoginViewV2')
        .message(error.message || 'Login failed')
        .severity(SEVERITY.CRITICAL)
        .custom('tags', {
          method,
          isFromMexApp: isMexApp,
        })
        .build();
      ddRum.addError(error, errorCtx);

      if (isMexApp) {
        // eslint-disable-next-line no-console
        console.log('[LOGIN DEBUG]', { mexInfo: getCommonMexAppFacet() });
      }
    },
    [method],
  );

  const redirectAfterLogin = () => {
    let redirectPath = ROUTES_V2_PATHS.home;
    if (loginRedirect) {
      redirectPath = loginRedirect;
    }
    if (!redirectPath.startsWith(APP_BASE_URL_PATH)) {
      redirectPath = APP_BASE_URL_PATH + redirectPath;
    }

    window.location.href = redirectPath;
  };

  const handleGrabIdLogin = useCallback(
    async () => {
      if (loginRedirect) {
        window.localStorage.setItem(STORAGE_LOGIN_REDIRECT, loginRedirect);
      }

      if (!redirectToLandingPageIfNoCountry()) {
        try {
          const isMexApp = isFromMexApp();

          if (isMexApp) {
            await scribeTrackEvent({
              stateName: SCRIBE_MEX_APP_STATES.EXPRESS_MEX_APP_LOGIN,
              eventName: SCRIBE_MEX_APP_EVENTS.DEFAULT,
              facets: {
                ...getCommonMexAppFacet(),
                [SCRIBE_EVENT_PARAMS.LOGIN_TYPE]: 'PAX_ID',
              },
              forceUpdate: true,
            });
          }

          paxLoginStartAction();
          const grabIdAuthClient = initGrabIDAuthClient();

          if (
            grabIdAuthClient &&
            typeof grabIdAuthClient.startAuthRequest === 'function'
          ) {
            grabIdAuthClient.startAuthRequest();
          } else {
            throw new Error('GrabID Auth client initialization failed');
          }
        } catch (error) {
          captureException(error);
        }
      }
    },
    [loginRedirect, captureException],
  );

  const handleGfbLogin = useCallback(
    async () => {
      const gfbLoginUrl = getLoginRedirectUrl();
      try {
        const isUserAuthenticated = await isUserAuthenticatedAsync();
        if (!isUserAuthenticated) {
          clearLoginStorage();
          window.location.href = gfbLoginUrl;
        } else {
          redirectAfterLogin();
        }
      } catch (err) {
        captureException(err);
        window.location.href = gfbLoginUrl;
      }
    },
    [captureException, redirectAfterLogin],
  );

  useEffect(
    () => {
      const initialiseLogin = async () => {
        try {
          if (method === GRAB_ID_LOGIN_METHOD) {
            handleGrabIdLogin();
          } else {
            handleGfbLogin();
          }
        } catch (error) {
          captureException(error);
        }
      };

      initialiseLogin();
    },
    [handleGrabIdLogin, handleGfbLogin, captureException, method],
  );

  return null;
};

export default LoginViewV2;
